// ============================================================
// liveDocFlush.ts
// Save-time helper for cards that contain live (Yjs) rich-text documents.
// A collab editor debounces its readable-HTML snapshot (~1.5s); Save must
// push that snapshot into the store BEFORE the autosave flush, otherwise the
// readable copy only lands when the card unmounts (after the flush) and rides
// the NEXT debounced autosave (~1s later).
//
// flushCollabSnapshots() fires every mounted editor's pending snapshot
// (→ its onSnapshot → updateCase / updateWitness). Wrapping it in flushSync
// commits that store update synchronously, so the case autosave EFFECT
// (useAutoSave, an effect on currentCase) stages the payload before the
// caller's flushAutoSave() runs; the extra macrotask yield is a belt-and-
// braces wait for any passive effect React still defers. Witness snapshots
// call scheduleWitnessSave directly, so flushWitnessAutoSave picks them up.
// ============================================================
import { flushSync } from 'react-dom'
import { flushCollabSnapshots } from './RichTextEditor'
import { useCasevoStore } from '../../store/useCasevoStore'

export const flushLiveDocSnapshots = async (): Promise<void> => {
  flushSync(() => { flushCollabSnapshots() })
  await new Promise<void>((resolve) => setTimeout(resolve, 0))
}

// After a Save's flush: did the save fail? The autosave hooks publish their
// failure to the store (useAutoSave publishError / the client + witness
// autosaves → setSaveError) and keep the edit queued for retry. A card must
// then STAY OPEN with a message instead of closing as if it had saved.
export const saveDidFail = (): boolean => !!useCasevoStore.getState().saveError
export const SAVE_FAILED_MSG = 'Couldn’t save — your changes are kept and will retry. Check your connection.'
